import { DateTime } from 'luxon';
import { getExplicitUserTimeZone } from 'utils/timezone';

const formatYYYYMMDD = (d = new Date()) => {
  const month = (d.getMonth() + 1).toString().padStart(2, '0');
  const day = d.getDate().toString().padStart(2, '0');
  const year = d.getFullYear().toString();
  return [year, month, day].join('-');
};

const userNow = () => {
  const zone = getExplicitUserTimeZone();
  return zone ? DateTime.now().setZone(zone) : DateTime.now();
};

const inUserZone = (dateTime: DateTime) => {
  const zone = getExplicitUserTimeZone();
  return zone ? dateTime.setZone(zone) : dateTime;
};

const formatCalendarDate = (dateTime: DateTime, now: DateTime) => {
  if (dateTime.year === now.year) {
    return `${dateTime.monthShort} ${dateTime.day}`;
  }
  return `${dateTime.monthShort} ${dateTime.day}, ${dateTime.year}`;
};

const formatShortDueDate = (date: Date, now = DateTime.now()) => {
  const zone = getExplicitUserTimeZone();
  const dateTime = inUserZone(DateTime.fromJSDate(date));
  return formatCalendarDate(dateTime, zone ? now.setZone(zone) : now);
};

const formatPickerDate = (date: Date, now = userNow()) => (
  formatCalendarDate(DateTime.fromJSDate(date, { zone: 'local' }), now)
);

const toDateTime = (value: Date | string | null | undefined) => {
  if (!value) {
    return null;
  }
  const dateTime = value instanceof Date ? DateTime.fromJSDate(value) : DateTime.fromISO(value);
  if (!dateTime.isValid) {
    return dateTime;
  }
  return inUserZone(dateTime);
};

const isDueDatePast = (
  value: Date | string | null | undefined,
  now: DateTime = userNow(),
) => {
  const dueAt = toDateTime(value);
  if (!dueAt?.isValid) {
    return false;
  }
  return dueAt.setZone(now.zone).startOf('day') < now.startOf('day');
};

const pickedDateTimeInUserZone = (date: Date) => {
  const local = DateTime.fromJSDate(date, { zone: 'local' });
  const zone = getExplicitUserTimeZone();
  if (!zone) {
    return local;
  }
  return DateTime.fromObject({
    year: local.year,
    month: local.month,
    day: local.day,
    hour: local.hour,
    minute: local.minute,
    second: local.second,
    millisecond: local.millisecond,
  }, { zone });
};

const toJSDate = (value: Date | string | null | undefined): Date | undefined => {
  if (!value) {
    return undefined;
  }
  return value instanceof Date ? value : DateTime.fromISO(value).toJSDate();
};

const toPickerDate = (value: Date | string | null | undefined): Date | undefined => {
  if (!value) {
    return undefined;
  }
  const zone = getExplicitUserTimeZone();
  if (!zone) {
    return toJSDate(value);
  }
  const dateTime = inUserZone(value instanceof Date ? DateTime.fromJSDate(value) : DateTime.fromISO(value));
  if (!dateTime.isValid) {
    return undefined;
  }
  return new Date(dateTime.year, dateTime.month - 1, dateTime.day);
};

const stringOrDateToJSDate = (value: Date | string): Date => (
  typeof value === 'string' ? new Date(value) : value
);

const toIsoString = (dateTime: DateTime): string | undefined => dateTime.toISO() ?? undefined;

export {
    formatPickerDate,
    formatShortDueDate,
    formatYYYYMMDD,
    isDueDatePast,
    pickedDateTimeInUserZone,
    stringOrDateToJSDate,
    toDateTime,
    toIsoString,
    toJSDate,
    toPickerDate,
    userNow
};
